/**
 * ElementIpPanel — Embedded IPAM panel for any CMDB element.
 * Shows active IP assignments and allows creating/deleting them without
 * leaving the element detail view.
 *
 * Props:
 *   objectType  string   "server" | "rack_element" | "workstation" | "printer" | "ip_phone"
 *   objectId    number   DB id of the element (undefined → disabled state)
 *   companyId   number
 *   elementName string   human-readable name shown in confirmation dialogs
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

// ── small helpers ─────────────────────────────────────────────────────────────

const TYPE_BADGE = {
  static:  { bg: 'rgba(99,102,241,.18)',  color: '#a5b4fc', label: 'estático' },
  dhcp:    { bg: 'rgba(245,158,11,.15)',  color: '#fcd34d', label: 'DHCP' },
  vip:     { bg: 'rgba(14,165,233,.15)',  color: '#7dd3fc', label: 'VIP' },
  carp:    { bg: 'rgba(168,85,247,.18)',  color: '#d8b4fe', label: 'CARP' },
  vrrp:    { bg: 'rgba(14,165,233,.15)',  color: '#7dd3fc', label: 'VRRP' },
  hsrp:    { bg: 'rgba(14,165,233,.15)',  color: '#7dd3fc', label: 'HSRP' },
  virtual: { bg: 'rgba(14,165,233,.15)',  color: '#7dd3fc', label: 'virtual' },
};

/* Tipos de asignación "virtuales": la misma IP puede vivir en varios CIs a la
   vez (CARP/VRRP/HSRP en un clúster HA). El backend salta el chequeo de
   conflicto para estos tipos (VIRTUAL_ASSIGNMENT_TYPES). */
const VIRTUAL_IP_TYPES = ['carp', 'vrrp', 'hsrp', 'vip', 'virtual'];

function TypeBadge({ type }) {
  const s = TYPE_BADGE[type] || { bg: 'rgba(255,255,255,.1)', color: 'rgba(255,255,255,.55)', label: type || '—' };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

const IFACE_BADGE = {
  bmc:  { icon: '🔧', bg: 'rgba(245,158,11,.15)', color: '#fcd34d', label: 'BMC / iLO' },
  mgmt: { icon: '🖥️', bg: 'rgba(14,165,233,.15)',  color: '#7dd3fc', label: 'Gestión' },
};

function IfaceBadge({ iref, objectType, nicMap = {}, bmcNicSet = new Set() }) {
  if (!iref) return <span style={{ color: 'rgba(255,255,255,.3)' }}>—</span>;
  const known = IFACE_BADGE[iref];
  if (known) {
    return (
      <span style={{ background: known.bg, color: known.color, borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {known.icon} {known.label}
      </span>
    );
  }
  if (iref.startsWith('team:')) {
    return (
      <span style={{ background: 'rgba(99,102,241,.18)', color: '#a5b4fc', borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
        🔗 {iref.replace('team:', '')}
      </span>
    );
  }
  if (objectType === 'server_team') {
    return (
      <span style={{ background: 'rgba(99,102,241,.18)', color: '#a5b4fc', borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
        🔗 {iref}
      </span>
    );
  }
  if (iref.startsWith('nic-')) {
    if (bmcNicSet.has(iref)) {
      const bmc = IFACE_BADGE.bmc;
      return (
        <span style={{ background: bmc.bg, color: bmc.color, borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {bmc.icon} {bmc.label}
        </span>
      );
    }
    const label = nicMap[iref];
    return (
      <span style={{ background: 'rgba(16,185,129,.15)', color: '#6ee7b7', borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
        🔌 {label || iref}
      </span>
    );
  }
  return <span style={{ color: 'rgba(255,255,255,.45)' }}>{iref}</span>;
}

function EmptyRow({ cols, text }) {
  return (
    <tr>
      <td colSpan={cols} style={{ textAlign: 'center', color: 'rgba(255,255,255,.3)', fontStyle: 'italic', fontSize: '0.82rem', padding: '1.2rem 0' }}>
        {text}
      </td>
    </tr>
  );
}

// ── IP helpers (pure JS, no lib) ────────────────────────────────────────────

function isValidIpv4(ip) {
  return /^(\d{1,3}\.){3}\d{1,3}$/.test((ip || '').trim()) &&
    (ip || '').trim().split('.').every(o => { const n = Number(o); return Number.isInteger(n) && n >= 0 && n <= 255; });
}

function _ipv4ToInt(ip) {
  return ip.trim().split('.').reduce((acc, oct) => ((acc << 8) | parseInt(oct, 10)) >>> 0, 0);
}

function ipv4InSubnet(ip, cidr) {
  if (!cidr || !cidr.includes('/')) return true;
  try {
    const [netAddr, prefix] = cidr.split('/');
    const prefixLen = parseInt(prefix, 10);
    if (prefixLen === 0) return true;
    const mask = (~0 << (32 - prefixLen)) >>> 0;
    return (_ipv4ToInt(ip) & mask) === (_ipv4ToInt(netAddr) & mask);
  } catch { return true; }
}

/* Una IP registrada está ocupada si no está libre o ya tiene asignaciones
   activas (el backend las devuelve sólo activas con include_assignments). */
function isIpOccupied(ip) {
  if (!ip) return false;
  if (ip.status && ip.status !== 'free') return true;
  return (ip.assignments || []).length > 0;
}

// ─────────────────────────────────────────────────────────────────────────────

const DFLT_FORM = {
  mode: 'auto',           // auto | manual | map | existing
  subnet_id: '',
  ip: '',
  ip_id: '',              // modo 'existing': IP ya registrada en IPAM
  interface_ref: '',
  is_primary: false,
  assignment_type: 'static',
  comments: '',
};

// ── main component ────────────────────────────────────────────────────────────

export default function ElementIpPanel({ objectType, objectId, companyId, elementName, interfaces = [], onCountChange, hasHardwarePorts = false, getStorageBlob = null, onBmcCreated = null }) {
  const [assignments, setAssignments] = useState([]);
  const [subnets,     setSubnets]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [subnetsLoaded, setSubnetsLoaded] = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState(DFLT_FORM);
  const [saving,      setSaving]      = useState(false);
  const [formError,   setFormError]   = useState('');
  const [deleting,    setDeleting]    = useState(null); // assignment id being deleted
  const [nextIp,      setNextIp]      = useState('');   // preview of next free IP
  const [nextIpLoader, setNextIpLoader] = useState(false);
  const [nicMap,       setNicMap]       = useState({});  // nic-{id} → human label
  const [bmcNicSet,    setBmcNicSet]    = useState(new Set()); // nic-{id} values that belong to BMC component
  const [bmcExists,    setBmcExists]    = useState(false); // true when server already has a physical BMC component
  const [ifaceOptions, setIfaceOptions] = useState(null);  // null = free-text; array = dropdown
  const [ifaceLoading, setIfaceLoading] = useState(false);
  const [ifaceCustom,  setIfaceCustom]  = useState('');
  const [mapData,      setMapData]      = useState(null);
  const [mapLoading,   setMapLoading]   = useState(false);
  const [ipValidation, setIpValidation] = useState({ format: null, inSubnet: null });
  const [publicIps,    setPublicIps]    = useState([]);
  const [loadingPublicIps, setLoadingPublicIps] = useState(false);
  const [onlyFreePublic,   setOnlyFreePublic]   = useState(true);
  const formRef = useRef(null);

  // ── NIC port map (servers and storage arrays) ─────────────────────────────
  // Los CI con componentes fisicos (servidor, cabina) eligen la interfaz de
  // una lista real de puertos; el resto escribe el nombre a mano.
  const usesComponentPorts = objectType === 'server' || hasHardwarePorts;

  useEffect(() => {
    if (!usesComponentPorts || !objectId || !companyId) return;
    api.getComponents(objectId, companyId)
      .then(res => {
        const map = {};
        const bmcSet = new Set();
        (res?.data || []).forEach(c => {
          if (c.component_type === 'bmc') {
            (c.ports || []).forEach(p => { bmcSet.add(`nic-${p.id}`); });
          } else if (c.component_type === 'nic' || c.component_type === 'hba') {
            (c.ports || []).forEach(p => {
              map[`nic-${p.id}`] = `${c.model || (c.component_type === 'hba' ? 'HBA' : 'NIC')} · ${p.label || `P${p.port_number}`}`;
            });
          }
        });
        setNicMap(map);
        setBmcNicSet(bmcSet);
      })
      .catch(() => {});
  }, [usesComponentPorts, objectId, companyId]);

  // load available interfaces for the current object
  const loadIfaceOptions = useCallback(async () => {
    if (!objectId || !companyId) return;
    setIfaceLoading(true);
    try {
      let opts;
      if (usesComponentPorts) {
        const isServer = objectType === 'server';
        // Las cabinas (rack_element con puertos) tambien guardan los
        // network_teams en el blob de storage; sin pedirlo aqui no salian.
        const storageFetch = getStorageBlob
          || (isServer ? api.getServerStorage.bind(api) : null);
        const [compRes, storRes] = await Promise.allSettled([
          api.getComponents(objectId, companyId),
          storageFetch ? storageFetch(objectId, companyId) : Promise.resolve(null),
        ]);
        const allComps = compRes.status === 'fulfilled' ? (compRes.value?.data || []) : [];
        // Servidor: sólo las NIC llevan IP. Cabina: los puertos iSCSI (NIC) y los
        // FC (HBA) son sus interfaces de datos, así que se listan también.
        const compTypes = isServer ? ['nic'] : ['nic', 'hba'];
        const compPorts = allComps
          .filter(c => compTypes.includes(c.component_type))
          .flatMap(c => (c.ports || []).map(p => ({
            value: `nic-${p.id}`,
            label: `${c.component_type === 'hba' ? '🔗' : '🔌'} ${c.model || (c.component_type === 'hba' ? 'HBA' : 'NIC')} · ${p.label || `Puerto ${p.port_number}`}`,
          })));
        const storData = storRes.status === 'fulfilled' ? storRes.value?.data?.data : null;
        const teams = Array.isArray(storData?.network_teams) ? storData.network_teams : [];
        const teamOpts = teams.map((t, i) => {
          const name = (t?.name || '').trim() || `team-${i + 1}`;
          return { value: `team:${name}`, label: `🔗 ${name}${t?.mode ? ` [${t.mode}]` : ''}` };
        });
        const bmcComp = allComps.find(c => c.component_type === 'bmc');
        setBmcExists(!!bmcComp);
        const bmcEntry = bmcComp && (bmcComp.ports || []).length > 0
          ? { value: `nic-${bmcComp.ports[0].id}`, label: `🔧 BMC / iLO / IPMI · ${bmcComp.ports[0].label || `P${bmcComp.ports[0].port_number}`}` }
          : { value: 'bmc', label: '🔧 BMC / iLO / IPMI' };
        opts = [
          { value: '',           label: '— sin interfaz —' },
          bmcEntry,
          { value: 'mgmt',       label: '🖥️ mgmt (gestión)' },
          ...teamOpts,
          ...compPorts,
          { value: '__custom__', label: '✏️ Otra (escribir)…' },
        ];
      } else if (interfaces && interfaces.length > 0) {
        opts = [
          { value: '', label: '— sin interfaz —' },
          ...interfaces.map(i => ({
            value: i.name ?? i,
            label: `${i.name ?? i}${i.iface_type ? ` (${i.iface_type})` : ''}`,
          })),
          { value: '__custom__', label: '✏️ Otra (escribir)…' },
        ];
      } else {
        opts = null; // fallback: plain text input
      }
      setIfaceOptions(opts);
    } catch {
      setIfaceOptions(null);
    } finally {
      setIfaceLoading(false);
    }
  }, [usesComponentPorts, objectType, objectId, companyId, interfaces, getStorageBlob]);

  // ── data loading ──────────────────────────────────────────────────────────

  const loadAssignments = useCallback(async () => {
    if (!objectId || !companyId) return;
    setLoading(true);
    try {
      const queries = [
        api.getIpamAssignments(companyId, {
          object_type: objectType,
          object_id: objectId,
        }),
      ];
      // For servers, also load team (bond) and rack_element assignments
      if (objectType === 'server') {
        queries.push(
          api.getIpamAssignments(companyId, {
            object_type: 'server_team',
            object_id: objectId,
          }),
          api.getIpamAssignments(companyId, {
            object_type: 'rack_element',
            object_id: objectId,
          }),
        );
      }
      const results = await Promise.all(queries);
      const all = results.flatMap(r => (r.data || []).filter(a => a.status === 'active'));
      setAssignments(all);
    } catch (e) {
      console.error('ElementIpPanel: failed to load assignments', e);
    } finally {
      setLoading(false);
    }
  }, [objectType, objectId, companyId]);

  const loadSubnets = useCallback(async () => {
    if (!companyId || subnetsLoaded) return;
    try {
      const res = await api.getIpamSubnets(companyId);
      setSubnets((res.data || []).filter(s => s.status === 'active'));
      setSubnetsLoaded(true);
    } catch (e) {
      console.error('ElementIpPanel: failed to load subnets', e);
    }
  }, [companyId, subnetsLoaded]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);
  // Eagerly load subnets so we can show the "no subnets" warning without opening the form
  useEffect(() => { if (objectId && companyId) loadSubnets(); }, [objectId, companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  // propagate current IP count to parent (e.g. to show badge on tab label)
  useEffect(() => { onCountChange?.(assignments.length); }, [assignments.length, onCountChange]);

  // when subnet changes in auto mode, preview next free IP
  useEffect(() => {
    if (form.mode !== 'auto' || !form.subnet_id) { setNextIp(''); return; }
    let cancelled = false;
    setNextIpLoader(true);
    api.getIpamSubnetNextIp(form.subnet_id, companyId, 1)
      .then(res => { if (!cancelled) setNextIp((res.data?.ips || [])[0] || 'Sin IPs libres'); })
      .catch(() => { if (!cancelled) setNextIp('Error al obtener IP libre'); })
      .finally(() => { if (!cancelled) setNextIpLoader(false); });
    return () => { cancelled = true; };
  }, [form.subnet_id, form.mode, companyId]);

  // load subnet map in 'map' mode
  useEffect(() => {
    if (form.mode !== 'map' || !form.subnet_id) { setMapData(null); return; }
    let cancelled = false;
    setMapLoading(true);
    api.getIpamSubnetMap(form.subnet_id, companyId, 512)
      .then(res => { if (!cancelled) setMapData(res.data || null); })
      .catch(() => { if (!cancelled) setMapData(null); })
      .finally(() => { if (!cancelled) setMapLoading(false); });
    return () => { cancelled = true; };
  }, [form.mode, form.subnet_id, companyId]);

  // load registered public IPs in 'existing' mode (IP suelta ya dada de alta)
  useEffect(() => {
    if (form.mode !== 'existing' || !companyId) return;
    let cancelled = false;
    setLoadingPublicIps(true);
    api.getIpamIPs(companyId, { scope: 'public', limit: 500 })
      .then(res => { if (!cancelled) setPublicIps(res.data || []); })
      .catch(() => { if (!cancelled) setPublicIps([]); })
      .finally(() => { if (!cancelled) setLoadingPublicIps(false); });
    return () => { cancelled = true; };
  }, [form.mode, companyId]);

  // real-time validation for manual IP
  useEffect(() => {
    if (form.mode !== 'manual') { setIpValidation({ format: null, inSubnet: null }); return; }
    const ip = form.ip.trim();
    if (!ip) { setIpValidation({ format: null, inSubnet: null }); return; }
    const formatOk = isValidIpv4(ip);
    let inSubnet = null;
    if (formatOk && form.subnet_id) {
      const sub = subnets.find(s => String(s.id) === String(form.subnet_id));
      if (sub?.cidr) inSubnet = ipv4InSubnet(ip, sub.cidr);
    }
    setIpValidation({ format: formatOk, inSubnet });
  }, [form.ip, form.mode, form.subnet_id, subnets]);

  // ── form handlers ─────────────────────────────────────────────────────────

  const openForm = () => {
    loadSubnets();
    loadIfaceOptions();
    setForm(DFLT_FORM);
    setFormError('');
    setNextIp('');
    setMapData(null);
    setIfaceCustom('');
    setIpValidation({ format: null, inSubnet: null });
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
  };

  const closeForm = () => { setShowForm(false); setFormError(''); };

  const patch = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    setFormError('');
    const isExisting = form.mode === 'existing';
    if (isExisting) {
      if (!form.ip_id) { setFormError('Selecciona una IP pública.'); return; }
      const chosen = publicIps.find(x => String(x.id) === String(form.ip_id));
      if (chosen && isIpOccupied(chosen)) {
        setFormError(`La IP ${chosen.ip} ya está ocupada: elige otra o libérala primero.`);
        return;
      }
    } else if (!form.subnet_id) {
      setFormError(
        subnetsLoaded && subnets.length === 0
          ? 'No hay subredes IPAM configuradas. Ve a IPAM → Subredes para crear una antes de asignar IPs.'
          : 'Selecciona una subred.'
      );
      return;
    }
    if (form.mode === 'manual') {
      const ip = form.ip.trim();
      if (!ip) { setFormError('Introduce una dirección IP.'); return; }
      if (!isValidIpv4(ip)) { setFormError('La dirección IP no es válida (formato incorrecto).'); return; }
      const sub = subnets.find(s => String(s.id) === String(form.subnet_id));
      if (sub?.cidr && !ipv4InSubnet(ip, sub.cidr)) {
        setFormError(`La IP ${ip} no pertenece a la subred ${sub.cidr}.`); return;
      }
    }
    if (form.mode === 'map' && !form.ip.trim()) { setFormError('Selecciona una IP en el mapa.'); return; }

    let ifaceRef = form.interface_ref === '__custom__' ? ifaceCustom.trim() : form.interface_ref.trim();

    setSaving(true);
    try {
      // Auto-promote BMC: if synthetic 'bmc' selected, create the physical component+port first
      if (ifaceRef === 'bmc' && usesComponentPorts) {
        const promoRes = await api.promoteBmc(objectId, companyId);
        const port = promoRes?.data?.ports?.[0];
        if (port?.id) {
          ifaceRef = `nic-${port.id}`;
          setBmcNicSet(prev => new Set([...prev, ifaceRef]));
          setBmcExists(true);
          // El formulario del servidor necesita enterarse de que ya hay
          // interfaz física BMC para poner la IP en modo lectura.
          onBmcCreated?.(promoRes?.data);
        }
      }
      const payload = {
        object_type: objectType,
        object_id: objectId,
        interface_ref: ifaceRef || null,
        is_primary: form.is_primary,
        assignment_type: form.assignment_type,
        comments: form.comments.trim() || null,
      };
      if (isExisting) {
        // IP ya registrada en IPAM: el backend la asigna por id y no exige subred.
        payload.ip_id = Number(form.ip_id);
      } else {
        payload.subnet_id = parseInt(form.subnet_id);
        if (form.mode === 'manual' || form.mode === 'map') {
          payload.ip = form.ip.trim();
        }
        // else: sin ip → el backend coge la siguiente libre de subnet_id
      }
      await api.createIpamAssignment(payload, companyId);
      await loadAssignments();
      closeForm();
    } catch (err) {
      setFormError(err.message || 'Error al crear la asignación.');
    } finally {
      setSaving(false);
    }
  };

  const [settingPrimary, setSettingPrimary] = useState(null); // assignment id being set as primary
  const [releaseNotice, setReleaseNotice] = useState('');

  const handleSetPrimary = async (asgn) => {
    if (asgn.is_primary) return; // already primary
    setSettingPrimary(asgn.id);
    try {
      await api.patchIpamAssignment(asgn.id, { is_primary: true }, companyId);
      await loadAssignments();
    } catch (err) {
      console.error('ElementIpPanel: failed to set primary', err);
    } finally {
      setSettingPrimary(null);
    }
  };

  const handleDelete = async (asgn) => {
    if (!window.confirm(`¿Eliminar la asignación de ${asgn.ip}${asgn.interface_ref ? ` (${asgn.interface_ref})` : ''} en ${elementName || objectType}?`)) return;
    setDeleting(asgn.id);
    try {
      const res = await api.deleteIpamAssignment(asgn.id, companyId);
      setAssignments(prev => prev.filter(a => a.id !== asgn.id));
      // Si la IP la comparten varios elementos (VIP de un cluster CARP, por
      // ejemplo) sigue asignada: se dice, para que no parezca que no se liberó.
      const otros = res?.still_assigned || [];
      setReleaseNotice(
        otros.length
          ? `${asgn.ip} se ha quitado de este elemento, pero sigue asignada a ${otros
              .map(o => o.name || `${o.object_type} #${o.object_id}`)
              .join(', ')}. Queda como “asignada” hasta que se libere en el último elemento.`
          : res?.ip_status === 'free'
            ? `${asgn.ip} liberada: vuelve a estar disponible como IP libre.`
            : `${asgn.ip} se ha quitado de este elemento.`
      );
    } catch (err) {
      alert('Error al eliminar: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  // ── disabled state ────────────────────────────────────────────────────────

  if (!objectId) {
    return (
      <div style={{ padding: '1.5rem', textAlign: 'center', color: 'rgba(255,255,255,.35)', fontSize: '0.85rem', background: 'rgba(255,255,255,.03)', borderRadius: 8, border: '1px dashed rgba(255,255,255,.12)' }}>
        <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: 8 }}>🌐</span>
        Guarda el elemento primero para gestionar sus IPs en IPAM.
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────

  const primaryCount = assignments.filter(a => a.is_primary).length;
  const visiblePublicIps = onlyFreePublic
    ? publicIps.filter(p => !isIpOccupied(p))
    : publicIps;
  const selectedPublic = publicIps.find(p => String(p.id) === String(form.ip_id)) || null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ── header bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'rgba(255,255,255,.45)' }}>
          <span>🌐</span>
          <strong style={{ color: '#e2e8f0' }}>IPs asignadas</strong>
          {!loading && (
            <span style={{ background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.55)', borderRadius: 12, padding: '1px 9px', fontSize: '0.75rem' }}>
              {assignments.length}
            </span>
          )}
          {primaryCount > 0 && (
            <span style={{ background: 'rgba(245,158,11,.15)', color: '#fcd34d', borderRadius: 12, padding: '1px 9px', fontSize: '0.75rem' }}>
              ★ {primaryCount} primaria{primaryCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={showForm ? closeForm : openForm}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '5px 14px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600,
            background: showForm ? 'rgba(255,255,255,.08)' : '#7c3aed', color: showForm ? 'rgba(255,255,255,.6)' : '#fff',
            border: 'none', cursor: 'pointer',
          }}
        >
          {showForm ? '✕ Cancelar' : '+ Añadir IP'}
        </button>
      </div>

      {releaseNotice && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.8rem',
          background: 'rgba(99,102,241,.12)', border: '1px solid rgba(129,140,248,.35)',
          color: '#c7d2fe', borderRadius: 6, padding: '7px 10px',
        }}>
          <span>ℹ</span>
          <span style={{ flex: 1 }}>{releaseNotice}</span>
          <button type="button" onClick={() => setReleaseNotice('')}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.5)', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
      )}

      {/* ── assignments table ── */}
      <div style={{ borderRadius: 10, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden', boxShadow: '0 1px 12px 0 rgba(0,0,0,0.35)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,.025)', borderBottom: '2px solid rgba(255,255,255,.07)' }}>
              {['IP', 'Subred', 'Interfaz', 'Tipo', 'DNS', ''].map((h, i) => (
                <th key={i} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'rgba(255,255,255,.4)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <EmptyRow cols={6} text="Cargando…" />
            )}
            {!loading && assignments.length === 0 && (
              <EmptyRow cols={6} text="Sin IPs asignadas" />
            )}
            {!loading && assignments.length === 0 && subnetsLoaded && subnets.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '6px 12px 10px' }}>
                  <div style={{ background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.2)', borderRadius: 6, padding: '6px 12px', fontSize: '0.75rem', color: 'rgba(245,158,11,.8)' }}>
                    ⚠ Sin subredes IPAM — ve a <strong>IPAM → Subredes</strong> para configurar los segmentos de red
                  </div>
                </td>
              </tr>
            )}
            {!loading && assignments.map(a => (
              <tr
                key={a.id}
                style={{
                  borderBottom: '1px solid rgba(255,255,255,.04)',
                  borderLeft: a.is_primary ? '3px solid #f59e0b' : '3px solid transparent',
                  background: a.is_primary ? 'rgba(245,158,11,.1)' : 'transparent',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = a.is_primary ? 'rgba(245,158,11,.2)' : 'rgba(99,102,241,.07)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = a.is_primary ? 'rgba(245,158,11,.1)' : 'transparent'; }}
              >
                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    title={a.is_primary ? 'IP primaria' : 'Marcar como primaria'}
                    disabled={settingPrimary === a.id}
                    onClick={() => handleSetPrimary(a)}
                    style={{
                      background: 'none', border: 'none', cursor: a.is_primary ? 'default' : 'pointer',
                      padding: 0, marginRight: 4, fontSize: '0.9rem', lineHeight: 1,
                      color: a.is_primary ? '#d97706' : '#cbd5e1',
                      opacity: settingPrimary === a.id ? 0.4 : 1,
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { if (!a.is_primary) e.currentTarget.style.color = '#f59e0b'; }}
                    onMouseLeave={e => { if (!a.is_primary) e.currentTarget.style.color = '#cbd5e1'; }}
                  >
                    {a.is_primary ? '★' : '☆'}
                  </button>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#818cf8' }}>{a.ip || '—'}</span>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  {a.subnet_cidr
                    ? <span style={{ display: 'inline-block', background: 'rgba(255,255,255,.06)', borderRadius: 5, padding: '2px 8px', fontFamily: 'monospace', fontSize: '0.75rem', color: 'rgba(255,255,255,.55)', border: '1px solid rgba(255,255,255,.1)' }}>{a.subnet_cidr}</span>
                    : <span style={{ color: 'rgba(255,255,255,.3)' }}>—</span>}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <IfaceBadge iref={a.interface_ref} objectType={a.object_type} nicMap={nicMap} bmcNicSet={bmcNicSet} />
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <TypeBadge type={a.assignment_type} />
                </td>
                <td style={{ padding: '8px 12px', color: 'rgba(255,255,255,.5)', fontSize: '0.78rem' }}>
                  {a.dns_name || <span style={{ color: 'rgba(255,255,255,.25)' }}>—</span>}
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                  <button
                    type="button"
                    disabled={deleting === a.id}
                    onClick={() => handleDelete(a)}
                    title="Eliminar asignación"
                    style={{
                      background: 'none', border: 'none', borderRadius: '50%',
                      width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      color: 'rgba(255,255,255,.3)', cursor: deleting === a.id ? 'not-allowed' : 'pointer',
                      opacity: deleting === a.id ? 0.4 : 1,
                      fontSize: '0.85rem', lineHeight: 1, transition: 'all 0.15s',
                      flexShrink: 0,
                    }}
                    onMouseEnter={e => { if (deleting !== a.id) { e.currentTarget.style.background = 'rgba(239,68,68,.18)'; e.currentTarget.style.color = '#f87171'; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,.3)'; }}
                  >
                    {deleting === a.id ? '…' : '🗑'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── add form ── */}
      {showForm && (
        <div ref={formRef} style={{ background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.25)', borderRadius: 8, padding: '1.2rem' }}>
          <div style={{ fontWeight: 600, color: '#a5b4fc', marginBottom: '0.9rem', fontSize: '0.85rem' }}>
            ➕ Nueva asignación de IP
          </div>

          {/* ── no subnets warning ── */}
          {form.mode !== 'existing' && subnetsLoaded && subnets.length === 0 && (
            <div style={{ background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', borderRadius: 6, padding: '10px 14px', marginBottom: '0.9rem', fontSize: '0.82rem', color: '#fcd34d', lineHeight: 1.5 }}>
              ⚠️ <strong>Sin subredes IPAM configuradas.</strong> Para asignar IPs manualmente necesitas crear bloques y subredes en{' '}
              <strong>IPAM → Subredes</strong> primero.<br />
              <span style={{ opacity: 0.75, fontSize: '0.78rem' }}>El agente puede registrar IPs automáticamente aunque no haya subredes, pero no quedarán vinculadas a un segmento de red.</span>
            </div>
          )}

            <div>
              <div className="row g-2">

              {/* mode selector */}
              <div className="col-md-12">
                <div style={{ display: 'flex', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
                  {[
                    ['auto',     'Auto-asignar (próxima libre)'],
                    ['manual',   'IP manual'],
                    ['map',      '🗺️ Mapa de IPs'],
                    ['existing', '☁️ IP pública registrada'],
                  ].map(([v, lbl]) => (
                    <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '0.82rem', fontWeight: form.mode === v ? 700 : 400, color: form.mode === v ? '#a5b4fc' : 'rgba(255,255,255,.45)' }}>
                      <input type="radio" name="mode" value={v} checked={form.mode === v} onChange={() => { patch('mode', v); patch('ip', ''); patch('ip_id', ''); }} />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>

              {/* existing registered public IP (mode=existing) */}
              {form.mode === 'existing' && (
                <div className="col-md-12">
                  <label className="form-label" style={{ fontSize: '0.78rem' }}>
                    IP pública registrada *
                    {loadingPublicIps && <span style={{ marginLeft: 6, color: 'rgba(255,255,255,.3)', fontSize: '0.7rem' }}>cargando…</span>}
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={form.ip_id}
                    onChange={e => patch('ip_id', e.target.value)}
                    style={{ fontFamily: 'monospace' }}
                    required
                  >
                    <option value="">Selecciona IP pública…</option>
                    {visiblePublicIps.map(p => {
                      const busy = isIpOccupied(p);
                      const owner = (p.assignments || [])[0];
                      return (
                        <option key={p.id} value={p.id} disabled={busy}>
                          {p.ip}{p.subnet_cidr ? ` · ${p.subnet_cidr}` : ' · sin subred'}{p.dns_name ? ` · ${p.dns_name}` : ''}{busy ? ` — OCUPADA (${owner ? `${owner.object_type} #${owner.object_id}` : 'asignada'})` : ' — libre'}
                        </option>
                      );
                    })}
                  </select>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: 'rgba(255,255,255,.5)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={onlyFreePublic} onChange={e => setOnlyFreePublic(e.target.checked)} />
                      Mostrar solo libres
                    </label>
                    <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,.35)' }}>
                      {publicIps.filter(p => !isIpOccupied(p)).length} libres de {publicIps.length}
                    </span>
                  </div>
                  {!loadingPublicIps && publicIps.length === 0 && (
                    <div style={{ marginTop: 6, background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', borderRadius: 6, padding: '8px 12px', fontSize: '0.78rem', color: '#fcd34d' }}>
                      No hay IPs públicas dadas de alta. Regístralas en <strong>IPAM → IPs</strong> (con Subred = Auto si es una IP suelta).
                    </div>
                  )}
                  {selectedPublic && (
                    <div style={{ marginTop: 6, fontSize: '0.73rem', color: 'rgba(255,255,255,.45)' }}>
                      Se asignará <strong style={{ fontFamily: 'monospace', color: '#6ee7b7' }}>{selectedPublic.ip}</strong>
                      {selectedPublic.subnet_cidr ? ` (subred ${selectedPublic.subnet_cidr})` : ' (sin subred)'}
                    </div>
                  )}
                </div>
              )}

              {/* subnet */}
              {form.mode !== 'existing' && (
              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Subred *</label>
                <select
                  className="form-select form-select-sm"
                  value={form.subnet_id}
                  onChange={e => patch('subnet_id', e.target.value)}
                  required
                >
                  <option value="">Selecciona subred…</option>
                  {subnets.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.cidr}{s.description ? ` — ${s.description}` : ''}{s.vlan_name ? ` [VLAN ${s.vlan_id}]` : ''}
                    </option>
                  ))}
                </select>
              </div>
              )}

              {/* IP field (manual) or preview (auto/map) */}
              {form.mode !== 'existing' && (
              <div className="col-md-6">
                {form.mode === 'manual' ? (
                  <>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>
                      Dirección IP *
                      {ipValidation.format === true && (
                        <span style={{ marginLeft: 6, color: ipValidation.inSubnet === false ? '#ef4444' : '#059669', fontSize: '0.72rem' }}>
                          {ipValidation.inSubnet === false ? '⚠ fuera de la subred' : ipValidation.inSubnet === true ? '✔ en subred' : '✔ formato OK'}
                        </span>
                      )}
                      {ipValidation.format === false && (
                        <span style={{ marginLeft: 6, color: '#ef4444', fontSize: '0.72rem' }}>⚠ IP inválida</span>
                      )}
                    </label>
                    <input
                      className={`form-control form-control-sm${ipValidation.format === false || ipValidation.inSubnet === false ? ' is-invalid' : ipValidation.inSubnet === true ? ' is-valid' : ''}`}
                      type="text"
                      placeholder="e.g. 10.0.1.100"
                      value={form.ip}
                      onChange={e => patch('ip', e.target.value)}
                      required
                      style={{ fontFamily: 'monospace' }}
                    />
                  </>
                ) : form.mode === 'map' ? (
                  <>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>IP seleccionada</label>
                    <div style={{
                      background: form.ip ? 'rgba(16,185,129,.12)' : 'rgba(255,255,255,.04)',
                      border: `1px solid ${form.ip ? 'rgba(16,185,129,.35)' : 'rgba(255,255,255,.1)'}`,
                      borderRadius: 6, padding: '6px 10px', fontFamily: 'monospace',
                      fontSize: '0.82rem', color: form.ip ? '#6ee7b7' : 'rgba(255,255,255,.3)', minHeight: 31, fontWeight: form.ip ? 700 : 400,
                    }}>
                      {form.ip || 'Haz clic en una IP libre del mapa'}
                    </div>
                  </>
                ) : (
                  <>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Próxima IP libre</label>
                    <div style={{
                      background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 6,
                      padding: '6px 10px', fontFamily: 'monospace', fontSize: '0.82rem', color: 'rgba(255,255,255,.5)', minHeight: 31,
                    }}>
                      {nextIpLoader ? <span style={{ color: 'rgba(255,255,255,.3)' }}>Calculando…</span>
                        : nextIp ? <span style={{ color: '#6ee7b7', fontWeight: 600 }}>{nextIp}</span>
                        : <span style={{ color: 'rgba(255,255,255,.25)' }}>Elige una subred</span>}
                    </div>
                  </>
                )}
              </div>
              )}

              {/* IP map grid (mode=map only) */}
              {form.mode === 'map' && (
                <div className="col-md-12">
                  {!form.subnet_id ? (
                    <div style={{ color: 'rgba(255,255,255,.3)', fontSize: '0.8rem', fontStyle: 'italic' }}>Selecciona primero una subred para ver el mapa.</div>
                  ) : mapLoading ? (
                    <div style={{ color: 'rgba(255,255,255,.3)', fontSize: '0.8rem' }}>Cargando mapa de IPs…</div>
                  ) : mapData ? (
                    <div>
                      {/* summary bar */}
                      <div style={{ display: 'flex', gap: 12, marginBottom: 5, fontSize: '0.71rem', color: 'rgba(255,255,255,.4)', flexWrap: 'wrap' }}>
                        <span style={{ color: '#166534', fontWeight: 600 }}>■ {mapData.summary?.free ?? 0} libres</span>
                        <span style={{ color: '#991b1b' }}>■ {mapData.summary?.assigned ?? 0} asignadas</span>
                        {(mapData.summary?.reserved ?? 0) > 0 && <span style={{ color: '#92400e' }}>■ {mapData.summary.reserved} reservadas</span>}
                        {mapData.truncated && <span style={{ color: '#f59e0b' }}>⚠ mostrando {mapData.limit} de {mapData.summary?.usable_ips} IPs</span>}
                      </div>
                      {/* IP grid */}
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 2,
                        maxHeight: 190, overflowY: 'auto',
                        padding: '6px', border: '1px solid rgba(255,255,255,.08)', borderRadius: 6, background: 'rgba(255,255,255,.03)',
                      }}>
                        {(mapData.generated_ips || []).map(entry => {
                          const isFree = entry.status === 'free' && !entry.is_gateway;
                          const isSelected = form.ip === entry.ip;
                          let bg, color, cursor, borderColor, title;
                          if (entry.is_gateway) {
                            bg = 'rgba(59,130,246,.25)'; color = '#93c5fd'; cursor = 'default'; borderColor = 'rgba(59,130,246,.4)';
                            title = `GW: ${entry.ip}`;
                          } else if (entry.status === 'assigned') {
                            bg = 'rgba(239,68,68,.25)'; color = '#fca5a5'; cursor = 'not-allowed'; borderColor = 'rgba(239,68,68,.4)';
                            const asgn = entry.assignments?.[0];
                            title = `Asignada: ${entry.ip}${asgn ? ` → ${asgn.object_name || asgn.object_type}` : ''}`;
                          } else if (entry.status === 'reserved') {
                            bg = 'rgba(245,158,11,.25)'; color = '#fcd34d'; cursor = 'not-allowed'; borderColor = 'rgba(245,158,11,.4)';
                            title = `Reservada: ${entry.ip}`;
                          } else {
                            bg = isSelected ? '#7c3aed' : 'rgba(16,185,129,.18)'; color = isSelected ? '#fff' : '#6ee7b7';
                            cursor = 'pointer'; borderColor = isSelected ? '#5b21b6' : 'rgba(16,185,129,.35)';
                            title = isSelected ? `Seleccionada: ${entry.ip}` : `Libre: ${entry.ip} — clic para seleccionar`;
                          }
                          return (
                            <button
                              key={entry.ip} type="button" title={title}
                              onClick={() => isFree && patch('ip', isSelected ? '' : entry.ip)}
                              style={{
                                padding: '2px 5px', borderRadius: 3,
                                fontSize: '0.65rem', fontFamily: 'monospace', fontWeight: isSelected ? 700 : 400,
                                background: bg, color, cursor,
                                border: `1px solid ${borderColor}`,
                                lineHeight: 1.5, whiteSpace: 'nowrap',
                                transition: 'all 0.08s',
                                pointerEvents: isFree ? 'auto' : 'none', opacity: isFree ? 1 : 0.65,
                              }}
                            >
                              {entry.ip}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 4, fontSize: '0.7rem', color: 'rgba(255,255,255,.4)' }}>
                        🟢 libre &nbsp; 🔴 asignada &nbsp; 🟡 reservada &nbsp; 🔵 gateway
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: 'rgba(255,255,255,.3)', fontSize: '0.8rem' }}>No se pudo cargar el mapa de la subred.</div>
                  )}
                </div>
              )}

              {/* interface ref */}
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '0.78rem' }}>
                  Interfaz <span style={{ color: 'rgba(255,255,255,.3)' }}>(opcional)</span>
                  {ifaceLoading && <span style={{ marginLeft: 6, color: 'rgba(255,255,255,.3)', fontSize: '0.7rem' }}>cargando…</span>}
                </label>
                {ifaceOptions !== null ? (
                  <>
                    <select
                      className="form-select form-select-sm"
                      value={form.interface_ref}
                      onChange={e => { patch('interface_ref', e.target.value); if (e.target.value !== '__custom__') setIfaceCustom(''); }}
                      disabled={ifaceLoading}
                    >
                      {ifaceOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {form.interface_ref === '__custom__' && (
                      <input
                        className="form-control form-control-sm mt-1"
                        type="text"
                        placeholder="eth0, eno1, bond0…"
                        value={ifaceCustom}
                        onChange={e => setIfaceCustom(e.target.value)}
                        style={{ fontFamily: 'monospace' }}
                        autoFocus
                      />
                    )}
                    {usesComponentPorts && bmcNicSet.has(form.interface_ref) && (
                      <div style={{ fontSize: '0.72rem', color: '#7dd3fc', background: 'rgba(14,165,233,.15)', borderRadius: 4, padding: '3px 8px', marginTop: 4 }}>
                        ✅ Componente BMC físico ya registrado en este equipo
                      </div>
                    )}
                    {usesComponentPorts && form.interface_ref === 'bmc' && (
                      <div style={{ fontSize: '0.72rem', color: '#fcd34d', background: 'rgba(245,158,11,.15)', borderRadius: 4, padding: '3px 8px', marginTop: 4 }}>
                        ⚡ Al guardar se creará automáticamente el componente BMC conectable
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    placeholder="eth0, eno1, bond0…"
                    value={form.interface_ref}
                    onChange={e => patch('interface_ref', e.target.value)}
                  />
                )}
              </div>

              {/* assignment type */}
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Tipo</label>
                <select className="form-select form-select-sm" value={form.assignment_type} onChange={e => patch('assignment_type', e.target.value)}>
                  <option value="static">Estático</option>
                  <option value="dhcp">DHCP</option>
                  <optgroup label="IP compartida / HA">
                    <option value="carp">CARP (pfSense / OPNsense)</option>
                    <option value="vrrp">VRRP</option>
                    <option value="hsrp">HSRP</option>
                    <option value="vip">VIP</option>
                    <option value="virtual">Virtual / otra compartida</option>
                  </optgroup>
                </select>
                {VIRTUAL_IP_TYPES.includes(form.assignment_type) && (
                  <div style={{ fontSize: '0.71rem', color: '#d8b4fe', marginTop: 4, lineHeight: 1.35 }}>
                    IP compartida: la misma dirección puede asignarse a varios nodos (el par HA).
                  </div>
                )}
              </div>

              {/* is primary */}
              <div className="col-md-4" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_primary} onChange={e => patch('is_primary', e.target.checked)} />
                  <span>★ IP primaria</span>
                </label>
              </div>

              {/* comments */}
              <div className="col-md-12">
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Comentario <span style={{ color: 'rgba(255,255,255,.3)' }}>(opcional)</span></label>
                <input
                  className="form-control form-control-sm"
                  type="text"
                  placeholder="Descripción breve…"
                  value={form.comments}
                  onChange={e => patch('comments', e.target.value)}
                />
              </div>
            </div>

            {formError && (
              <div style={{ marginTop: '0.7rem', background: 'rgba(220,38,38,.12)', color: '#f87171', border: '1px solid rgba(220,38,38,.25)', borderRadius: 6, padding: '6px 12px', fontSize: '0.8rem' }}>
                ⚠ {formError}
              </div>
            )}

            <div style={{ marginTop: '0.9rem', display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  padding: '6px 18px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600,
                  background: '#7c3aed', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Guardando…' : 'Guardar asignación'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                style={{ padding: '6px 14px', borderRadius: 6, fontSize: '0.82rem', background: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.55)', border: '1px solid rgba(255,255,255,.12)', cursor: 'pointer' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
